import { Injectable } from '@angular/core';
import Swal, { SweetAlertOptions } from 'sweetalert2';

interface AlertOptions {
  popup: string;
  title: string;
  header: string;
  container: string;
  htmlContainer: string;
  actions: string;
  cancelButton: string;
  denyButton: string;
  confirmButton: string;
  icon: string;
  input: string;
  inputLabel: string;
  validationMessage: string;
}

@Injectable({
  providedIn: 'root',
})
export class AlertService {
  private customClass: AlertOptions = {
    popup:
      '!relative !transform !overflow-hidden !rounded-lg !bg-background !text-left !shadow-xl !transition-all sm:!my-8 sm:!w-fit sm:!max-w-lg !p-0 !gap-x-6 !grid-cols-[min-content] !border !border-primary !min-w-[380px]',
    title:
      '!p-0 !pt-3 !text-center sm:!text-left !text-base !font-semibold !leading-5 !text-foreground !pl-4 !pr-4 sm:!pr-6 sm:!pl-0 sm:!pt-6 !col-start-1 sm:!col-start-2 !col-end-3',
    header: 'p-0 text-foreground',
    container: 'p-0',
    htmlContainer:
      '!mt-2 sm:!mt-0 !m-0 !text-center sm:!text-left !text-sm !text-muted-foreground !pl-4 !pt-0 sm:!pl-0 !pr-4 !pb-4 sm:!pr-6 sm:!pb-4 !col-start-1 sm:!col-start-2 !col-end-3',
    actions:
      '!bg-card !px-4 !py-2 sm:!flex sm:!flex-row-reverse sm:!px-6 !w-full !justify-start !mt-0 !col-start-1 !col-end-3 !text-muted-foreground border-t border-border',
    cancelButton:
      'mt-3 sm:ml-3 inline-flex w-full justify-center rounded-md bg-background !hover:!bg-card px-3 py-2 text-sm font-semibold text-foreground shadow-sm border border-border sm:mt-0 sm:w-auto',
    denyButton:
      'inline-flex w-full justify-center rounded-md bg-destructive px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-destructive-500 sm:ml-3 sm:w-auto',
    confirmButton:
      'inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold shadow-sm sm:ml-3 sm:w-auto',
    icon: '!m-0 !mx-auto !flex !h-8 !w-16 !flex-shrink-0 !items-center !justify-end !rounded-full !border-0 !mt-5 sm!mt-6 !col-start-1 !col-end-3 sm:!col-end-2',
    input:
      'my-2 !mx-0 !mr-6 !col-start-2 !col-end-3 !text-sm rounded ml-[-60px] w-[350px]',
    inputLabel: '!text-sm font-semibold !col-start-1 !col-end-2',
    validationMessage:
      'text-danger bg-danger/20 swal2-validation-message alert alert-light-danger w-full !col-start-1 !col-end-3',
  };
  private svgIcons = {
    warning: `
      <div class="!m-0 !mx-auto !flex !h-12 !w-12 !flex-shrink-0 !items-center !justify-center !rounded-full !bg-warning/20 !border-0 sm:!h-10 sm:!w-10 !mt-5 sm!mt-6 !col-start-1 !col-end-3 sm:!col-end-2 swal2-icon-show">
        <svg class="h-6 w-6 text-warning" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"></path>
        </svg>
      </div>
    `,
    success: `
      <div class="!m-0 !mx-auto !flex !h-12 !w-12 !flex-shrink-0 !items-center !justify-center !rounded-full !bg-green-200 !border-0 sm:!h-10 sm:!w-10 !mt-5 sm!mt-6 !col-start-1 !col-end-3 sm:!col-end-2 swal2-icon-show">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6 text-green-600">
          <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
        </svg>
      </div>
    `,
    error: `
      <div class="!m-0 !mx-auto !flex !h-12 !w-12 !flex-shrink-0 !items-center !justify-center !rounded-full !bg-red-200 !border-0 sm:!h-10 sm:!w-10 !mt-5 sm!mt-6 !col-start-1 !col-end-3 sm:!col-end-2 swal2-icon-show">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6 text-red-600">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
        </svg>
      </div>
    `,
    info: `
      <div class="!m-0 !mx-auto !flex !h-12 !w-12 !flex-shrink-0 !items-center !justify-center !rounded-full !bg-blue-200 !border-0 sm:!h-10 sm:!w-10 !mt-5 sm!mt-6 !col-start-1 !col-end-3 sm:!col-end-2 swal2-icon-show">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6 text-blue-600">
          <path stroke-linecap="round" stroke-linejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
        </svg>
      </div>
    `,
    question: `
      <div class="!m-0 !mx-auto !flex !h-12 !w-12 !flex-shrink-0 !items-center !justify-center !rounded-full !bg-gray-200 !border-0 sm:!h-10 sm:!w-10 !mt-5 sm!mt-6 !col-start-1 !col-end-3 sm:!col-end-2 swal2-icon-show">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6 text-gray-600">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008v.008H12v-.008Z" />
        </svg>
      </div>
    `,
  };
  private swal = Swal.mixin({
    customClass: this.customClass,
    buttonsStyling: true,
    reverseButtons: true,
    padding: '1em',
  });

  constructor() {}

  public fire(options: SweetAlertOptions) {
    // Allow callers to supply a fully custom `iconHtml` (e.g. branded modals) without forcing a preset SVG.
    if (options.icon && !options.iconHtml) {
      const preset = this.svgIcons[options.icon as keyof typeof this.svgIcons];
      if (preset) {
        options.iconHtml = preset;
      }
    }
    return this.swal.fire(options);
  }

  public warning(title: string, text: string) {
    return this.fire({
      title: title,
      text: text,
      icon: 'warning',
      showConfirmButton: true,
      confirmButtonColor: 'hsl(var(--primary))',
    });
  }
}
